/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/


import { INodeProps, Module, Node } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';
import { ISearchCategoryViewProps, ISearchFormViewProps, ISearchKeywordViewProps, ISearchProductViewProps } from '../../../modules/smwe-search/./components';
import { ISearchViewProps } from '../../../modules/smwe-search/./smwe-search';

const SearchView: React.FC<ISearchViewProps> = props => {
    const {
        Search,
        form,
        SearchForm,
        FormWrapper,
        label
    } = props;

    React.useEffect(() => {
        document.body.addEventListener('keydown', (e: any) => handleKeyDown(e));
    });

    return (
        <Module {...Search}>
            {label}
            <Node {...SearchForm}>
                {_renderForm(form as ISearchFormViewProps, FormWrapper, props)}
            </Node>
        </Module>
    );
};

const handleKeyDown = (e: React.KeyboardEvent<HTMLElement>): void => {
    const showClass = document.getElementsByClassName('ms-search__form bx-show');
    if (showClass) {
        //Fetch node list from which required elements could be grabbed as needed.
        const focusable = document.getElementById('SearchFormModal');
        const elements = focusable?.querySelectorAll('input, a, select, textarea, li, [tabindex]:not([tabindex="-1"]), button') as NodeListOf<HTMLDivElement>;
        const firstFocusable = elements[0];
        const lastFocusable = elements[elements.length - 1];

        if (e.ctrlKey || e.altKey) {
            return;
        }

        const keys = {
            9: () => { //9 = TAB
                if (e.shiftKey && e.target === firstFocusable) {
                    lastFocusable.focus();
                    console.log('Last Focusable');
                    e.preventDefault();
                }

                if (e.target === lastFocusable) {
                    firstFocusable.focus();
                    console.log('First Focusable');
                    e.preventDefault();
                }
            }
        };
        if (keys[e.keyCode]) {
            keys[e.keyCode]();
        }
    }
};

const _renderForm = (form: ISearchFormViewProps, FormWrapper: INodeProps, props: ISearchViewProps) => {
    const {
        AutoSuggestAriaLabel,
        AutoSuggestAriaLabelText,
        searchText,
        AutoSuggest,
        KeywordSuggest,
        ProductSuggest,
        CategorySuggest,
        UlKeyword,
        UlProduct,
        UlCategory,
        autosuggestCategory,
        autosuggestKeyword,
        autosuggestProduct,
    } = props;
    return (
        <Node {...FormWrapper}>
            {form.submitBtn}
            <div className='ms-search__form-placeholder' />
            <div className='ms-search__form-container'>
                {form.label}
                {form.input}
                <Node {...AutoSuggest}>
                    {searchText && searchText.length > 0 ?
                        <Node {...AutoSuggestAriaLabel}>{AutoSuggestAriaLabelText}</Node> : ''
                    }
                    {_renderKeywordSuggestions(KeywordSuggest, UlKeyword, autosuggestKeyword)}
                    {_renderProductSuggestions(ProductSuggest, UlProduct, autosuggestProduct)}
                    {_renderCategorySuggestions(CategorySuggest, UlCategory, autosuggestCategory)}
                </Node>
            </div>
            {form.cancelBtn}
        </Node>
    );
};

const _renderKeywordSuggestions = (
    KeywordSuggest: INodeProps,
    UlKeyword: INodeProps,
    keywordSuggestions?: ISearchKeywordViewProps
) => {
    return (
        keywordSuggestions && (
            <Node {...KeywordSuggest}>
                <Node {...UlKeyword}>
                    {keywordSuggestions.text.map(text => {
                        return text;
                    })}
                </Node>
            </Node>
        )
    );
};

const _renderProductSuggestions = (
    ProductSuggest: INodeProps,
    UlProduct: INodeProps,
    productSuggestions?: ISearchProductViewProps
) => {
    return (
        productSuggestions && (
            /* eslint-disable react/no-unknown-property */
            <Node {...ProductSuggest}>
                <Node {...UlProduct}>
                    {productSuggestions.title}
                    {productSuggestions.items.map((item, index) => {
                        return (
                            <Node {...item.LiProduct} key={item.id || index}>
                                <Node {...item.AProduct}>
                                    {item.thumbnail}
                                    {item.text}
                                    <span className='msc-autoSuggest__productResults-item-price'>{item.price}</span>
                                </Node>
                            </Node>
                        );
                    })}
                </Node>
            </Node>
        )
    );
};

const _renderCategorySuggestions = (
    CategorySuggest: INodeProps,
    UlCategory: INodeProps,
    categorySuggestions?: ISearchCategoryViewProps
) => {
    return (
        categorySuggestions && (
            <Node {...CategorySuggest}>
                <Node {...UlCategory}>
                    {categorySuggestions.title}
                    {categorySuggestions.text.map(text => {
                        return text;
                    })}
                </Node>
            </Node>
        )
    );
};

export default SearchView;